import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
  getBlogById,
  createBlog,
  editBlog,
  deleteBlog,
} from "../utils/methods";
import { QuillEditor, UploadImage, InputComp } from "../components";
import { strings } from "../utils/strings";
import UserIcon from "../components/Icons/userIcon";
import Button from "../components/Button";
import Autocomplete from "@mui/material/Autocomplete";
import TextField from "@mui/material/TextField";
import useFetchOptions from "../hooks/useFetchOptions";
import { toast } from "react-toastify";

const BlogEdit = () => {
  const { id: currentId } = useParams();
  const navigate = useNavigate();
  const [blogData, setBlogData] = useState({});
  const [name, setName] = useState("");
  const [type, setType] = useState("");
  const [title, setTitle] = useState("");
  const [disable, setDisable] = useState("");
  const [bloggerImageUrl, setBloggerImageUrl] = useState("");
  const [blogImageUrl, setBlogImageUrl] = useState("");
  const [bloggerImageFile, setBloggerImageFile] = useState(null);
  const [blogImageFile, setBlogImageFile] = useState(null);

  const { bloggers, blogTypes, loading, error } = useFetchOptions();

  useEffect(() => {
    const loadBlogData = async () => {
      if (currentId !== "new") {
        try {
          const blog = await getBlogById(currentId);
          setBlogData(blog);
          setName(blog.bloggerName || "");
          setType(blog.type || "");
          setTitle(blog.title || "New Blog");
          setBlogImageUrl(blog.blogImage || "");
          setBloggerImageUrl(blog.bloggerImage || "");
        } catch (error) {
          console.error("Error loading blog:", error);
        }
      }
    };
    loadBlogData();
  }, [currentId]);

  const handleTitleChange = (key, newTitle) => {
    setTitle(newTitle);
    setBlogData((prevData) => ({ ...prevData, [key]: newTitle }));
  };

  const handleInputChange = (field, value) => {
    setBlogData((prevData) => ({ ...prevData, [field]: value }));
  };

  const handleDescChange = (key, newValue) => {
    setBlogData((prevData) => ({ ...prevData, [key]: newValue }));
  };

  const handleOptionChange = (key, newValue) => {
    setBlogData((prevData) => ({
      ...prevData,
      [key]: newValue || "",
    }));

    // Update corresponding state if necessary
    switch (key) {
      case "name":
        setName(newValue || "");
        break;
      case "type":
        setType(newValue || "");
        break;
      default:
        break;
    }
  };

  const handleImageUpload = (url, file) => {
    setBlogImageUrl(url);
    setBlogImageFile(file);
    setBlogData((prevData) => ({ ...prevData, blogImage: url }));
  };

  const handleBloggerImageUpload = (url, file) => {
    setBloggerImageUrl(url);
    setBloggerImageFile(file);
    setBlogData((prevData) => ({ ...prevData, bloggerImage: url }));
  };

  const formatTitleToId = (title) => {
    return title.trim().replace(/\s+/g, "-").toLowerCase();
  };

  const handleSave = async () => {
    const fieldsToValidate = [
      {
        key: "title",
        value: blogData.title,
        label: strings["inputTitle"],
        regex: null,
      },
      {
        key: "desc",
        value: blogData.desc,
        label: strings["inputDesc"],
        regex: null,
      },
      {
        key: "type",
        value: type,
        label: strings["inputCategory"],
        regex: null,
      },
      { key: "name", value: name, label: strings["inputBlogger"], regex: null },
      {
        key: "blogImage",
        value: blogData.blogImage,
        label: strings["blogImage"],
        regex: null,
      },
      {
        key: "bloggerImage",
        value: blogData.bloggerImage,
        label: strings["bloggerImage"],
        regex: null,
      },
      {
        key: "template",
        value: blogData.template,
        label: "Blog's Content",
        regex: null,
      },
    ];

    let isFormValid = true;

    fieldsToValidate.forEach(({ key, value, label, regex }) => {
      if (!value || (regex && !regex.test("should ", () => {})(value))) {
        isFormValid = false;
        toast.error(`${label} is required and must be valid.`);
      }
    });

    if (!isFormValid) return;

    try {
      const newBlogId = formatTitleToId(title);
      if (currentId !== "new" && currentId !== newBlogId) {
        setDisable(true);
        await createBlog({ ...blogData, title }, newBlogId);
        await deleteBlog(currentId);
      } else if (currentId === "new") {
        setDisable(true);
        await createBlog({ ...blogData, title }, newBlogId);
      } else {
        setDisable(true);
        await editBlog(currentId, blogData, blogImageFile, bloggerImageFile);
      }
      navigate(`/blog`);
    } catch (error) {
      setDisable(false);
      console.error("Error saving blog:", error);
    }
  };
  if (loading) return <p>Loading...</p>;
  if (error) return <p>Error loading options: {error.message}</p>;
  return (
    <div className="bg-border w-full h-full overflow-scroll xl:px-[212px] lg:px-[100px] md:px-[60px] pt-12 pb-[60px]">
      <p className="text-[36px] font-semibold">
        {currentId === "new"
          ? `${strings["addBlogTitle"]} ${blogData.title || "New"}`
          : `${strings["EditBlogTitle"]} ${blogData.title}`}
      </p>

      {/* Title */}

      <InputComp
        label={strings["inputTitle"]}
        placeHolder={strings["titlePlaceholder"]}
        type="text"
        value={title}
        handleChange={handleTitleChange}
        inputKey="title"
        labelClasses="text-sm font-medium pb-[6px] pt-5 text-color-1"
        classes="h-11 text-color-1 border-color-9 hover:border-black border-1"
      />

      {/* Description */}
      <InputComp
        label={strings["inputDesc"]}
        placeHolder={strings["descriptionPlaceholder"]}
        type="text"
        value={blogData.desc || ""}
        handleChange={handleDescChange}
        inputKey="desc"
        labelClasses="text-sm font-medium pb-[6px] pt-5 text-color-1"
        classes="h-11 text-color-1 border-color-9 hover:border-black border-1"
      />

      {/* Type Selector */}
      <div className="mt-5">
        <div className="mb-[6px]">
          <label className="text-sm font-medium text-color-1">
            {strings["inputCategory"]}
          </label>
        </div>
        <Autocomplete
          className="w-full"
          options={blogTypes}
          getOptionLabel={(option) => option || ""}
          value={type}
          onChange={(event, newValue) => handleOptionChange("type", newValue)}
          disableClearable // Removes the clear button
          renderInput={(params) => (
            <TextField
              {...params}
              placeholder="Select"
              sx={{
                "& .MuiOutlinedInput-root": {
                  height: 44,
                  padding: 0,
                  paddingLeft: 1,
                  borderRadius: "8px",
                  backgroundColor: "white",
                  display: "flex",
                  alignItems: "center",
                },
                "& .MuiInputBase-input": {
                  height: "100%",
                  padding: "0 12px",
                  textAlign: "start",
                  boxSizing: "border-box",
                },
              }}
            />
          )}
        />
      </div>

      {/* Bloggers Name Selector */}
      <div className="mt-5">
        <div className="mb-[6px]">
          <label className="text-sm font-medium pb-[6px] mb-2 text-color-1">
            {strings["inputBlogger"]}
          </label>
        </div>
        <Autocomplete
          className="w-full"
          options={bloggers}
          getOptionLabel={(option) => option || ""}
          value={name}
          onChange={(event, newValue) => handleOptionChange("name", newValue)}
          disableClearable // Removes the clear button
          renderOption={(props, option) => (
            <li {...props} className="flex items-center gap-x-2">
              <UserIcon />
              <div className="pt-1">{option}</div>
            </li>
          )}
          renderInput={(params) => (
            <TextField
              {...params}
              placeholder="Select"
              sx={{
                "& .MuiOutlinedInput-root": {
                  height: 44,
                  padding: 0,
                  paddingLeft: 1,
                  borderRadius: "8px",
                  backgroundColor: "white",
                  display: "flex",
                  alignItems: "center",
                },
                "& .MuiInputBase-input": {
                  height: "100%",
                  padding: "0 12px",
                  textAlign: "start",
                  boxSizing: "border-box",
                },
              }}
            />
          )}
        />
      </div>

      <div className="mt-[20px]">
        <div className="mb-[6px]">
          <label className="text-sm font-medium pb-[6px] mb-2 text-color-1 mt-[20px]">
            {strings["blogImage"]}
          </label>
        </div>

        {/* Blog Image */}
        <UploadImage
          onImageUpload={handleImageUpload}
          blogId={currentId === "new" ? null : currentId}
          imageType="blogImages"
          initialImageUrl={blogData.blogImage} // Pass the initial image URL
        />
      </div>

      <div className="mt-[20px]">
        <div className="mb-[6px]">
          <label className="text-sm font-medium pb-[6px] mb-2 text-color-1">
            {strings["bloggerImage"]}
          </label>
        </div>

        {/* Blogger Image */}

        <UploadImage
          onImageUpload={handleBloggerImageUpload}
          blogId={currentId === "new" ? null : currentId}
          imageType="bloggerImages"
          initialImageUrl={blogData.bloggerImage} // Pass the initial image URL
        />
      </div>

      {/* HTML Editor */}
      <div className="my-6 bg-white ">
        <QuillEditor
          value={blogData.template || ""}
          onChange={(content) => handleInputChange("template", content)}
        />
      </div>

      {/* Upload Button */}

      <div className="flex justify-center w-full">
        <Button
          classes={`!w-full justify-center ${
            disable ? "opacity-50 cursor-not-allowed" : ""
          }`}
          onClick={disable ? null : handleSave}
          disabled={disable}
        >
          {strings["submitButton"]}
        </Button>
      </div>
    </div>
  );
};

export default BlogEdit;
